import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Td,
  Text,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import Card from 'components/card/Card';
import { FaCheckCircle, FaStar, FaUserSlash } from 'react-icons/fa';
import { MdCancel, MdInfo, MdVerified } from 'react-icons/md';
import { useFirebaseAuth } from '../../../auth/hooks/useFirebaseAuth';
import useApprovePartner from 'domains/admin/hooks/useApprovePartner';
import useRejectPartner from 'domains/admin/hooks/useRejectPartner';
import useDisableUser from 'domains/admin/hooks/useDisableUser';
import { useAssignValidatorToUser } from 'domains/admin/hooks/useAssignValidatorToUser';
import AdminInfo from './AdminInfo';
import { useTranslation } from 'i18n/TranslationProvider';
import useChangeClientVIPStatus from 'domains/admin/hooks/useChangeClientVIPStatus';

const AllUsersItem = ({ user, refetch, tableMode = false }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { uid } = useFirebaseAuth();
  const isClient = user.role === 'client';
  const { approvePartner, loading: approveLoading } = useApprovePartner();
  const { rejectPartner, loading: rejectLoading } = useRejectPartner();
  const { disableUser, loading: disableLoading } = useDisableUser();
  const { assignValidatorToUser } = useAssignValidatorToUser();
  const {
    changeClientVIPStatus,
    loading: markAsVIPLoading,
    error: markAsVIPError,
  } = useChangeClientVIPStatus({ onSettled: refetch });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  const [actionType, setActionType] = useState(null);

  const showToast = (title, status) => {
    toast({
      title,
      status,
      duration: 3000,
      isClosable: true,
    });
  };

  const handleApprove = async () => {
    const success = await approvePartner(user.firebaseUid);

    showToast(
      success ? t('User approved') : t('Error approving user'),
      success ? 'success' : 'error',
    );

    if (success) {
      await assignValidatorToUser(user.firebaseUid);
      await refetch();
    }

    return success;
  };

  const handleReject = async () => {
    const success = await rejectPartner(user.firebaseUid);

    showToast(
      success ? t('User rejected') : t('Error rejecting user'),
      success ? 'success' : 'error',
    );

    if (success) {
      await assignValidatorToUser(user.firebaseUid);
      await refetch();
    }

    return success;
  };

  const handleDisable = async () => {
    const success = await disableUser(user.firebaseUid);

    showToast(
      success ? t('User disabled') : t('Error disabling user'),
      success ? 'success' : 'error',
    );

    if (success) {
      await assignValidatorToUser(user.firebaseUid);
      await refetch();
    }

    return success;
  };

  const handleMarkAsVIP = async () => {
    const nextVIPStatus = !user.isVIP;
    const success = await changeClientVIPStatus(user.firebaseUid, nextVIPStatus);
    if (success) {
      showToast(
        nextVIPStatus ? t('User marked as VIP') : t('User unmarked as VIP'),
        'success',
      );
    } else {
      showToast(
        markAsVIPError ||
          (nextVIPStatus
            ? t('Error marking user as VIP')
            : t('Error unmarking user as VIP')),
        'error',
      );
    }
  };

  const approveSafely = async () => {
    try {
      await handleApprove();
    } catch {
      showToast(t('Failed to approve user'), 'error');
    } finally {
      onClose();
    }
  };

  const rejectSafely = async () => {
    try {
      await handleReject();
    } catch {
      showToast(t('Failed to reject user'), 'error');
    } finally {
      onClose();
    }
  };

  const disableSafely = async () => {
    try {
      await handleDisable();
    } catch {
      showToast(t('Failed to disable user'), 'error');
    } finally {
      onClose();
    }
  };

  const markAsVIPSafely = async () => {
    try {
      await handleMarkAsVIP();
    } catch {
      showToast(
        user.isVIP
          ? t('Failed to unmark user as VIP')
          : t('Failed to mark user as VIP'),
        'error',
      );
    } finally {
      onClose();
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge>{t('Admin')}</Badge>;
      case 'partner':
        return <Badge>{t('Partner')}</Badge>;
      case 'client':
        return <Badge>{t('Client')}</Badge>;
      default:
        return <Badge>{t('N/A')}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge colorScheme="green">{t('Approved')}</Badge>;
      case 'pending':
        return <Badge colorScheme="yellow">{t('Pending')}</Badge>;
      case 'rejected':
        return <Badge colorScheme="red">{t('Rejected')}</Badge>;
      case 'disabled':
        return <Badge colorScheme="gray">{t('Disabled')}</Badge>;
      default:
        return <Badge>{t('Unknown')}</Badge>;
    }
  };

  const actions = (
    <Flex gap={2} flexWrap="wrap" justify={tableMode ? 'flex-start' : 'flex-end'}>
      <Tooltip label={t('Approve')}>
        <Button
          colorScheme="green"
          size="sm"
          w="36px"
          h="36px"
          borderRadius="full"
          onClick={() => {
            setActionType('approve');
            onOpen();
          }}
          isDisabled={approveLoading || user.validationStatus === 'approved'}
        >
          <Icon as={FaCheckCircle} />
        </Button>
      </Tooltip>

      <Tooltip label={t('Reject')}>
        <Button
          colorScheme="red"
          size="sm"
          w="36px"
          h="36px"
          borderRadius="full"
          onClick={() => {
            setActionType('reject');
            onOpen();
          }}
          isDisabled={rejectLoading || user.validationStatus === 'rejected'}
        >
          <Icon as={MdCancel} />
        </Button>
      </Tooltip>

      <Tooltip label={t('Disable')}>
        <Button
          colorScheme="gray"
          size="sm"
          w="36px"
          h="36px"
          borderRadius="full"
          onClick={() => {
            setActionType('disable');
            onOpen();
          }}
          isDisabled={disableLoading || user.validationStatus === 'disabled'}
        >
          <Icon as={FaUserSlash} />
        </Button>
      </Tooltip>

      {isClient && (
        <Tooltip label={user.isVIP ? t('Unmark as VIP') : t('Mark as VIP')}>
          <Button
            colorScheme="purple"
            size="sm"
            w="36px"
            h="36px"
            borderRadius="full"
            onClick={() => {
              setActionType('mark-as-vip');
              onOpen();
            }}
            isDisabled={markAsVIPLoading}
          >
            <Icon as={FaStar} />
          </Button>
        </Tooltip>
      )}

    </Flex>
  );

  const statusCell = (
    <Flex align="center" gap={2} flexWrap="wrap">
      {getStatusBadge(user.validationStatus)}
      {user.validationStatus === 'approved' && user.role === 'partner' && (
        <Tooltip
          hasArrow
          label={
            <Box>
              <Text>{t('Partner validated by:')}</Text>
              <AdminInfo adminId={user.validatedBy} />
              <Text mt={1}>
                {user.validatedAt && !isNaN(new Date(user.validatedAt).getTime())
                  ? new Date(user.validatedAt).toLocaleString()
                  : t('Invalid Date')}
              </Text>
            </Box>
          }
        >
          <span>
            <Icon as={MdInfo} color="blue.500" boxSize={4} />
          </span>
        </Tooltip>
      )}
    </Flex>
  );

  const emailCell = (
    <Flex align="center" gap={2} flexWrap="wrap">
      <Text fontSize="sm">{user.email}</Text>
      {user.firebaseUid === uid && (
        <Text fontSize="xs" color="gray.500">
          {t('(you)')}
        </Text>
      )}
      {user.isEmailVerified && (
        <Tooltip label={t('User email has been verified')} hasArrow>
          <span>
            <Icon as={MdVerified} color="blue.500" boxSize={4} />
          </span>
        </Tooltip>
      )}
      {user.isVIP && (
        <Tooltip label={t('VIP')} hasArrow>
          <span>
            <Icon as={FaStar} color="yellow.300" boxSize={4} />
          </span>
        </Tooltip>
      )}
    </Flex>
  );

  return (
    <>
      {tableMode ? (
        <Tr>
          <Td>
            <Text fontSize="sm" fontWeight="700" isTruncated>
              {user.displayName || t('No name')}
            </Text>
          </Td>
          <Td>{emailCell}</Td>
          <Td>
            <Text fontSize="sm">{user.companyName || '-'}</Text>
          </Td>
          <Td>{getRoleBadge(user.role)}</Td>
          <Td>{statusCell}</Td>
          <Td>{actions}</Td>
        </Tr>
      ) : (
        <Card p="4" mb="4" borderRadius="2xl" shadow="sm">
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            gap={4}
          >
            <Box minW={0} flex="1">
              <Text fontSize="md" fontWeight="bold" isTruncated>
                {user.displayName || t('No name')}
              </Text>
              {emailCell}
            </Box>
            <Flex align="center" gap={2} flexWrap="wrap">
              {getRoleBadge(user.role)}
              {statusCell}
            </Flex>
            {actions}
          </Flex>
        </Card>
      )}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Confirm action')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {actionType === 'approve' && t('Are you sure you want to approve this user?')}
              {actionType === 'reject' && t('Are you sure you want to reject this user?')}
              {actionType === 'disable' && t('This will disable the user account. Continue?')}
              {actionType === 'mark-as-vip' &&
                (user.isVIP
                  ? t('Are you sure you want to unmark this client as VIP?')
                  : t('Are you sure you want to mark this client as VIP?'))}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {t('Cancel')}
              </Button>

              {actionType === 'approve' && (
                <Button
                  colorScheme="green"
                  ml={3}
                  isLoading={approveLoading}
                  onClick={approveSafely}
                >
                  {t('Confirm')}
                </Button>
              )}

              {actionType === 'reject' && (
                <Button
                  colorScheme="red"
                  ml={3}
                  isLoading={rejectLoading}
                  onClick={rejectSafely}
                >
                  {t('Reject')}
                </Button>
              )}

              {actionType === 'disable' && (
                <Button
                  colorScheme="gray"
                  ml={3}
                  isLoading={disableLoading}
                  onClick={disableSafely}
                >
                  {t('Disable')}
                </Button>
              )}

              {actionType === 'mark-as-vip' && (
                <Button
                  colorScheme="purple"
                  ml={3}
                  isLoading={markAsVIPLoading}
                  onClick={markAsVIPSafely}
                >
                  {t('Confirm')}
                </Button>
              )}

            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AllUsersItem;
